import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Box, Dropdown, Input, Text } from 'samba-ui'
import styled from 'styled-components'
import { buyOrContentTypeFeedsOptions, FEED_TYPE, FEED_VERSIONS, GEO_DATA_SOURCE, getFeedTypeOption } from '../../../../constants'
import { selectAllFeeds } from '../../../../selectors'
import { Label } from '../styles'
import { InputContainer, StyledMainContainer } from './styles'
import { getMinDateWithReportConfig } from '../../../../utils'

const StyledInput = styled(Input)`
  width: 100%;
`;

export const NO_PANEL_JOIN_COUNTRIES = ['IN', 'BR', 'MX'];

const initialPbseVersionOptions = [FEED_VERSIONS.v1_4_2_Harvester];

function ClientAndType({
  clients,
  feed,
  onChangeFeed,
  clientId,
  reportConfigs,
}) {
  const [feedId, setFeedId] = useState("");
  const [feedIdError, setFeedIdError] = useState("");
  const [pbseVersionOptions, setPbseVersionOptions] = useState(initialPbseVersionOptions);

  const countryMemo = useMemo(() => _.get(feed, "country.value"), [feed]);
  const clientIdMemo = useMemo(() => _.get(feed, "client_id"), [feed]);
  /** @type {FEED_TYPE} */
  const typeMemo = useMemo(() => _.get(feed, "type"), [feed]);


  const onChangePbseVersion = useCallback((option) => {
    onChangeFeed("pbse_version", option);

    if (buyOrContentTypeFeedsOptions[typeMemo]) {
      onChangeFeed('selectedBuyOrContentType', buyOrContentTypeFeedsOptions[typeMemo][option.value === FEED_VERSIONS.v1_7_0.value ? 0 : 1])
    }
  }, [onChangeFeed, typeMemo])

  useEffect(() => {
    if (clientId && !clientIdMemo) {
      onChangeFeed("client_id", clientId);
    }
  }, [clientId, clientIdMemo, onChangeFeed]);

  useEffect(() => {
    let options = _.clone(initialPbseVersionOptions)

    if (countryMemo === "FR" && typeMemo === "spot exposure") {
      options = [FEED_VERSIONS.v1_4_2]
    }

    if (["GB", "AU"].includes(countryMemo) && typeMemo === "spot exposure") {
      options.push(FEED_VERSIONS.v1_4_3)
    }

    if (countryMemo === "US") {
      options = [FEED_VERSIONS.v1_4_5, FEED_VERSIONS.v1_4_4, FEED_VERSIONS.v1_4_2_Kantar]
    }

    if (["AU", "DE", "ES", "GB", "IT"].includes(countryMemo)) {
      options.push(FEED_VERSIONS.v1_4_6)
    }

    if (["AU"].includes(countryMemo)) {
      options.push(FEED_VERSIONS.v1_4_7)
    }

    if ([
      'all viewership',
      'all viewership ad',
      'all viewership content',
      'tivo all viewership',
      'tivo all viewership ad',
      'tivo all viewership content',
    ].includes(typeMemo)) {
      options = [FEED_VERSIONS.v2_1_0]
    }

    if (typeMemo === "content viewership") {
      options = _.compact([
        ['US', 'AU', 'DE', 'ES', 'FR', 'IT', 'GB'].includes(countryMemo) && FEED_VERSIONS.v1_6_1,
        ['CA'].includes(countryMemo) && FEED_VERSIONS.v1_6_0,
        ["AU", "DE", "ES", "FR", "GB", "IT"].includes(countryMemo) && FEED_VERSIONS.v1_4_1,
        FEED_VERSIONS.v1_4_0,
      ]);
    }

    if (typeMemo === "tivo spot exposure") {
      options = _.filter(options, { value: "1.4.4" })
    }

    if (typeMemo === 'tivo content viewership') {
      options = [FEED_VERSIONS.v1_2_0]
    }

    if (typeMemo === 'geo weights') {
      options = [FEED_VERSIONS.v1_6_8_geo_weights, FEED_VERSIONS.v2_0_1_geo_weights]
    }

    if (typeMemo === "spot exposure") {
      const arr = [
        ['AU', 'US'].includes(countryMemo) && FEED_VERSIONS.v1_6_7,
        ['AU', 'CA', 'DE', 'ES', 'FR', 'GB', 'IT'].includes(countryMemo) && FEED_VERSIONS.v1_6_6,
        // ['US'].includes(countryMemo) && FEED_VERSIONS.v1_6_5,
        // ['CA'].includes(countryMemo) && FEED_VERSIONS.v1_6_2,
      ]
      options = [..._.compact(arr), ...options]
    }

    // Temporary, replace with 2.0.0 when that branch is merged.
    if (typeMemo === 'streaming geo panel') {
      options = [{
        value: '2.0.0', label: '2.0.0',
      }];
    }

    setPbseVersionOptions(options);
    
    onChangePbseVersion(_.first(options))

  }, [clientId, onChangeFeed, countryMemo, typeMemo, clientIdMemo, onChangePbseVersion])

  const onFeedIdChange = (e) => {
    const { value } = e.target;
    setFeedId(value);
    if (!value) {
      onChangeFeed("feed_id", "");
      setFeedIdError("");
      return;
    }

    const validationRegex = /^[a-zA-Z0-9\w]+$/;
    const isValid = !!value.match(validationRegex);
    if (!isValid) {
      onChangeFeed("feed_id", "error");
      setFeedIdError(`Feed id can only contain alphanumeric characters`);
      return;
    }

    onChangeFeed("feed_id", value);
    setFeedIdError("");
  };

  const getFeedOptions = () => {
    const {
      country: { value },
    } = feed;

    const allViewershipOptions = [
      getFeedTypeOption('all viewership'),
      getFeedTypeOption('all viewership ad'),
      getFeedTypeOption('all viewership content'),
    ]

    if (NO_PANEL_JOIN_COUNTRIES.includes(value)) {
      return allViewershipOptions
    }

    let feedTypeOptions = [
      ...allViewershipOptions,
      getFeedTypeOption('spot exposure'),
      getFeedTypeOption('content viewership'),
      getFeedTypeOption('geo weights'),
      getFeedTypeOption('x_ref'),
      getFeedTypeOption('streaming geo panel'),
    ];

    if (value === "US") {
      feedTypeOptions = [
        ...feedTypeOptions,
        getFeedTypeOption('tivo all viewership'),
        getFeedTypeOption('tivo all viewership ad'),
        getFeedTypeOption('tivo all viewership content'),
        { label: "Tivo Ad Exposure", value: "tivo spot exposure" },
        { label: "Tivo Content Viewership", value: "tivo content viewership" },
        { label: "Tivo X-Ref", value: "tivo x_ref" },
        { label: "Tivo+Acr X-Ref", value: "tivo+acr x_ref" },

      ];
    }
    if (value === "ES" || value === "FR") {
      feedTypeOptions = [
        ...allViewershipOptions,
        { label: "TV Ad Exposure File", value: "spot exposure" },
        { label: "Geo Weights", value: "geo weights" },
        { label: "X-Ref", value: "x_ref" },
        { label: "Content Viewership", value: "content viewership" },
        getFeedTypeOption('streaming geo panel'),
      ];
    } else if (value === "US") {
      feedTypeOptions = [
        ...feedTypeOptions,
        {
          label: "Demographic Panel",
          value: "demographic panel",
        },
      ];
    }

    return feedTypeOptions;
  };


  const showGeoDataSourceMemo = useMemo(() =>
    typeMemo === "geo weights" && ["AU", 'DE', 'ES', 'GB', 'IT'].includes(countryMemo),
    [countryMemo, typeMemo])

  useEffect(() => {
    const feedClone = _.clone(feed)
    if (showGeoDataSourceMemo) {
      feedClone.misc_json = { ...feedClone.misc_json, geo_data_source: GEO_DATA_SOURCE[0].value }
    } else {
      delete feedClone.misc_json.geo_data_source
    }
    if (_.differenceWith(feed, feedClone, _.isEqual)) {
      onChangeFeed('misc_json', feedClone.misc_json)
    }
  }, [showGeoDataSourceMemo, typeMemo])

  const clientOptions = clients
    .sort((a, b) => {
      const name1 = a.name.toLowerCase();
      const name2 = b.name.toLowerCase();
      if (name1 < name2) return -1;
      if (name1 > name2) return 1;
      return 0;
    })
    .map((client) => ({
      value: client.id,
      label: client.name,
    }));


  const metaOptions = pbseVersionOptions.map(option => {

    const { 
      minDate: configMinDate 
    } = getMinDateWithReportConfig({
      type: typeMemo,
      country: {
        value: countryMemo
      },
      pbse_version: {
        value: option.value
      }
    }, reportConfigs)

    const metaLabel = configMinDate 
      ? `${option.label} [${configMinDate.format('YYYY-MM-DD')}]` 
      : option.label

    return {
      value: option.value,
      label: metaLabel
    }
  })
  
  return (
    <StyledMainContainer>
      <InputContainer>
        <Label>Client</Label>
        <Dropdown
          options={clientOptions}
          placeholder="Select..."
          selected={clientOptions.find(
            (option) => option.value === feed.client_id
          )}
          onSelect={(option) => {
            onChangeFeed("client_id", option.value);
          }}
        />
      </InputContainer>
      <InputContainer>
        <Label>Feed type</Label>
        <Dropdown
          options={getFeedOptions().sort((a, b) =>
            a.label.localeCompare(b.label)
          )}
          selected={getFeedOptions().find(
            (option) => option.value === feed.type
          )}
          placeholder="Select..."
          onSelect={(option) => {
            console.log(option)
            onChangeFeed('type', option.value)
            onChangeFeed('tivo_report', option.value === "tivo spot exposure")
          }
          }
        />
      </InputContainer>

      {
        showGeoDataSourceMemo &&
        <InputContainer>
          <Label>Geo Data Source</Label>
          <Dropdown
            options={GEO_DATA_SOURCE}
            selected={GEO_DATA_SOURCE.find(({ value }) => value === feed.misc_json.geo_data_source)}
            placeholder="Select..."
            onSelect={(option) => {

              onChangeFeed('misc_json', {
                ...feed.misc_json,
                geo_data_source: option.value
              })
            }
            }
          />
        </InputContainer>
      }

      <InputContainer>
        <Label>Feed id</Label>
        <StyledInput value={feedId} onChange={onFeedIdChange} />
        {feedIdError && (
          <Text fontSize={1} fontWeight={400} color="alert">
            {feedIdError}
          </Text>
        )}
      </InputContainer>
      {((feed.type.includes("spot exposure") || feed.type.includes("all viewership") || feed.type === "content viewership") || feed.type === 'geo weights') && (
        <Box mt={3}>
          <InputContainer>
            <Label>Select a Version</Label>
            <Dropdown
              isClearable={true}
              options={metaOptions}
              placeholder="Select a pbse version..."
              selected={feed.pbse_version}
              onSelect={onChangePbseVersion}
            />
          </InputContainer>
        </Box>
      )}
    </StyledMainContainer>
  );
}

ClientAndType.propTypes = {
  feeds: PropTypes.array,
  reportConfigs: PropTypes.array,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      bucket_path: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
      feed_ids: PropTypes.arrayOf(PropTypes.number),
    })
  ),
  feed: PropTypes.shape({
    client_id: PropTypes.number,
    country: PropTypes.object,
    name: PropTypes.string,
  }),
  onChangeFeed: PropTypes.func,
  clientId: PropTypes.number,
};

const mapStateToProps = (store) => ({
  feeds: selectAllFeeds(store),
});

export default connect(mapStateToProps)(ClientAndType);
